import { Component, Input, OnInit, signal, ChangeDetectorRef, ChangeDetectionStrategy } from "@angular/core";

import { TypeManagerDecorator } from "../../main/type.map.service";
import { Workflow as WorkflowTypes } from "./service/Workflow";
import { S25RuleTreeModule } from "../s25-rule-tree/s25.rule.tree.module";
import { DropDownItem } from "../../pojo/DropDownItem";
import { DataIngestionService } from "../data-ingestion/data.ingestion.service";
import { INGESTION_POINT_TYPE_ID } from "../data-ingestion/data.ingestion.const";

@TypeManagerDecorator("workflow-action-rules")
@Component({
    selector: "workflow-action-rules",
    template: `
        <div class="action-rules-container">
            @if (selectedAction) {
                @let hasFormContext = !!formContext()?.itemId;
                <div class="form-selector">
                    <label>Form Context:</label>
                    <div class="form-controls">
                        @if (hasFormContext) {
                            <div class="form-context-info">
                                <strong>{{ formContext()?.itemName }}</strong>
                                <p>Using the form context selected for this action.</p>
                            </div>
                        } @else {
                            <div class="no-form-context-warning">
                                <p>
                                    <strong>No Form Context Selected:</strong> Please select a form context for this
                                    action to configure rules.
                                </p>
                            </div>
                        }
                    </div>
                </div>

                @if (hasFormContext) {
                    <div class="rules-editor">
                        @if (selectedAction.workflowActionId) {
                            <s25-ng-rule-tree
                                [category]="'workflowAction'"
                                [subCategory]="getActionSubCategory(selectedAction)"
                                [subCategoryPrefix]="getActionSubCategoryPrefix(selectedAction)"
                                [conditionFilterMap]="getActionConditionFilterMap()"
                                class="rule-tree-editor"
                            ></s25-ng-rule-tree>
                        } @else {
                            <div class="unsaved-action-warning">
                                <p>
                                    <strong>Action Not Saved:</strong> Please save the workflow process first before
                                    configuring rules for this action.
                                </p>
                            </div>
                        }
                    </div>
                } @else {
                    <div class="no-form-selected">
                        <p>Select a form context for this action to configure rules.</p>
                    </div>
                }
            } @else {
                <div class="no-action">
                    <p>No action selected.</p>
                </div>
            }
        </div>
    `,
    styles: `
        .action-rules-container {
            padding: 20px;
        }

        .rules-header h3 {
            margin: 0 0 16px 0;
            color: var(--color--text-strong);
        }

        .form-selector {
            margin-bottom: 20px;
        }

        .form-selector label {
            display: block;
            margin-bottom: 8px;
            font-weight: 500;
            color: var(--color--text-strong);
        }

        .form-controls {
            padding: 12px;
            background: var(--color--surface-2-bg);
            border: 1px solid var(--color--border);
            border-radius: 6px;
        }

        .form-context-info {
            color: var(--color--registration);
        }

        .form-context-info strong {
            display: block;
            margin-bottom: 4px;
        }

        .form-context-info p {
            margin: 0;
            font-size: 14px;
        }

        .no-form-context-warning {
            color: var(--color--danger);
        }

        .no-form-context-warning p {
            margin: 0;
            font-size: 14px;
        }

        .unsaved-action-warning {
            color: var(--color--surface-bg);
            background: var(--color--warning);
            border: 1px solid var(--color--warning);
            border-radius: 6px;
            padding: 16px;
            text-align: center;
        }

        .unsaved-action-warning p {
            margin: 0;
            font-size: 14px;
        }

        .rules-editor {
            background: var(--color--surface-bg);
            border: 1px solid var(--color--border);
            border-radius: 6px;
            padding: 16px;
        }

        .rule-tree-editor {
            min-height: 200px;
        }

        .no-form-selected {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
            background: var(--color--surface-2-bg);
            border-radius: 6px;
        }

        .no-action {
            text-align: center;
            padding: 40px 20px;
            color: var(--color--text-muted);
        }
    `,
    standalone: true,
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [S25RuleTreeModule],
})
export class WorkflowActionRulesComponent implements OnInit {
    @Input() selectedAction!: WorkflowTypes.WorkflowAction;
    @Input() workflowProcessId!: number;

    formContext = signal<DropDownItem | null>(null);

    async ngOnInit() {
        await this.loadFormContext();
    }

    async loadFormContext() {
        if (this.selectedAction.actionItemId && this.selectedAction.actionItemTypeId === INGESTION_POINT_TYPE_ID) {
            const dropdownItem: DropDownItem = {
                itemId: this.selectedAction.actionItemId.toString(),
                itemTypeId: this.selectedAction.actionItemTypeId,
                itemName: `Form ${this.selectedAction.actionItemId}`,
            };

            try {
                const ingestionPoint = await DataIngestionService.getIngestionPoint(this.selectedAction.actionItemId);
                if (ingestionPoint?.ingestionPointName) {
                    dropdownItem.itemName = ingestionPoint.ingestionPointName;
                }
                this.formContext.set(dropdownItem);
            } catch (error) {
                console.error("Error loading ingestion point:", error);
                this.formContext.set(dropdownItem);
            }
        } else {
            this.formContext.set({
                itemId: this.selectedAction.actionItemId || 0,
                itemTypeId: this.selectedAction.actionItemTypeId || 0,
                itemName: `Context ${this.selectedAction.actionItemId || 0}`,
            } as DropDownItem);
        }
    }

    getActionSubCategoryPrefix(action: WorkflowTypes.WorkflowAction): string {
        if (!action.workflowActionId) {
            return "";
        }
        return `${action.workflowActionId}&`;
    }

    getActionSubCategory(action: WorkflowTypes.WorkflowAction): string {
        const formContext = this.formContext();
        if (!action.workflowActionId || !formContext?.itemId) {
            return "";
        }
        // Format: actionId & parentItemTypeId & parentItemId
        return `${action.workflowActionId}&${INGESTION_POINT_TYPE_ID}&${formContext.itemId}`;
    }

    getActionConditionFilterMap(): Record<string, string> {
        const formContext = this.formContext();
        if (!formContext?.itemId) return {};
        return {
            ingestionFieldsAll: "" + formContext.itemId,
        };
    }
}
